import { Controller } from "@hotwired/stimulus"

// 구독 컨트롤러
// 구독 페이지의 인터랙션을 처리합니다.
export default class extends Controller {
  static targets = [
    "planName", 
    "planPrice", 
    "planDescription", 
    "planFeatures", 
    "confirmMessage", 
    "currentPlanName", 
    "newPlanName", 
    "priceDifference",
    "newCardForm"
  ]
  
  static values = {
    planId: String,
    planName: String,
    planPrice: Number,
    planDescription: String,
    priceDifference: Number,
    currentPlanId: String,
    currentPlanName: String,
    termsRequiredMessage: String,
    paymentMethodRequiredMessage: String,
    cardNumberRequiredMessage: String,
    expiryRequiredMessage: String,
    cvcRequiredMessage: String,
    cardHolderRequiredMessage: String,
    noExtraCostLabel: String,
    upgradeConfirmText: String,
    downgradeConfirmText: String,
    changeConfirmText: String,
    // Optional i18n overrides
    features: String,
    featureFreeRules: String,
    featureFreeDmIncluded: String,
    featureFreeBasicSupport: String,
    featurePaidUnlimitedRules: String,
    featurePaidUnlimitedDm: String,
    featurePaidPrioritySupport: String,
    featurePaidUsageBilling: String
  }

  connect() {
    // 초기화 로직
  }

  // 구독 모달 열기
  openSubscribeModal(event) {
    // 플랜 정보 설정
    const planId = event.currentTarget.dataset.subscriptionPlanIdParam
    const planName = event.currentTarget.dataset.subscriptionPlanNameParam
    const planPrice = event.currentTarget.dataset.subscriptionPlanPriceParam
    const planDescription = event.currentTarget.dataset.subscriptionPlanDescriptionParam
    const priceDifference = event.currentTarget.dataset.subscriptionPriceDifferenceParam || 0
    
    // Value 설정
    this.planIdValue = planId
    this.planNameValue = planName
    this.planPriceValue = planPrice
    this.planDescriptionValue = planDescription
    this.priceDifferenceValue = priceDifference
    
    // 플랜 정보 표시
    if (this.hasPlanNameTarget) {
      this.planNameTarget.textContent = planName
    }
    
    if (this.hasPlanPriceTarget) {
      this.planPriceTarget.textContent = this.formatCurrency(planPrice)
    }
    
    if (this.hasPlanDescriptionTarget) {
      this.planDescriptionTarget.textContent = planDescription || ""
    }
    
    // 플랜 기능 로드
    if (this.hasPlanFeaturesTarget) {
      this.loadPlanFeatures(planId)
    }
    
    // 새 카드 폼 이벤트 처리
    this.setupPaymentMethodEvents()
    
    // 모달 열기
    const modal = document.getElementById("subscribe-modal")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.open()
      }
    }
  }
  
  // 플랜 기능 로드
  loadPlanFeatures(planId) {
    // MVP: 간단한 플랜 기능 표시 (FREE/PAID)
    const planName = this.data.get('planName') || 'FREE'

    // 1) Try features mapping passed via data-subscription-features-value (JSON)
    let provided = null
    try {
      if (this.hasFeaturesValue && this.featuresValue) {
        provided = JSON.parse(this.featuresValue)
      }
    } catch (_) {
      provided = null
    }

    // 2) Build sensible English defaults
    const usageUnitPrice = Number(document.body.dataset?.usageUnitPrice || 0)
    const usagePriceLabel = this.formatCurrency(usageUnitPrice, { precision: 3 })
    const defaults = {
      'FREE': [
        this.featureFreeRulesValue || 'Up to 5 monitoring rules',
        (this.featureFreeDmIncludedValue || 'Includes %{count}/month auto DMs').replace('%{count}', Number(document.body.dataset?.freeDmLimit || 100)),
        this.featureFreeBasicSupportValue || 'Basic templates & support'
      ],
      'PAID': [
        this.featurePaidUnlimitedRulesValue || 'Unlimited monitoring rules',
        (this.featurePaidUnlimitedDmValue || 'Unlimited auto DMs (%{price} per DM)').replace('%{price}', usagePriceLabel),
        this.featurePaidPrioritySupportValue || 'Priority support',
        this.featurePaidUsageBillingValue || 'Usage-based billing'
      ]
    }

    const features = provided || defaults
    const planFeatures = features[planName] || features['FREE']
    
    if (this.planFeaturesTarget) {
      this.planFeaturesTarget.innerHTML = ""
      
      planFeatures.forEach(featureName => {
        const li = document.createElement("li")
        li.className = "flex items-center space-x-3"
        li.innerHTML = `
          <svg class="flex-shrink-0 h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          <span class="text-sm text-gray-700">${featureName}</span>
        `
        this.planFeaturesTarget.appendChild(li)
      })
    }
  }

  formatCurrency(amount, { precision = null, showCode = true } = {}) {
    const symbol = document.body.dataset.billingCurrencySymbol || "$"
    const code = document.body.dataset.billingCurrencyCode || "USD"
    const defaultPrecision = parseInt(document.body.dataset.billingCurrencyPrecision || "2", 10)
    const value = Number(amount)

    if (Number.isNaN(value)) {
      return `${symbol}${amount}`
    }

    const fractionDigits = precision !== null ? precision : defaultPrecision
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    })

    return showCode ? `${symbol}${formatted} ${code}` : `${symbol}${formatted}`
  }
  
  // 결제 수단 이벤트 설정
  setupPaymentMethodEvents() {
    // 새 결제 수단 라디오 버튼 이벤트
    const newPaymentRadio = document.getElementById("new-payment-method")
    if (newPaymentRadio) {
      newPaymentRadio.addEventListener("change", () => {
        if (this.hasNewCardFormTarget) {
          if (newPaymentRadio.checked) {
            this.newCardFormTarget.classList.remove("hidden")
          } else {
            this.newCardFormTarget.classList.add("hidden")
          }
        }
      })
    }
    
    // 기존 결제 수단 라디오 버튼 이벤트
    const existingPaymentRadios = document.querySelectorAll('input[name="payment-method"]:not(#new-payment-method)')
    existingPaymentRadios.forEach(radio => {
      radio.addEventListener("change", () => {
        if (this.hasNewCardFormTarget && radio.checked) {
          this.newCardFormTarget.classList.add("hidden")
        }
      })
    })
  }

  // 구독 모달 닫기
  closeSubscribeModal() {
    const modal = document.getElementById("subscribe-modal")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.close()
      }
    }
  }

  // 구독 처리
  processSubscription() {
    // 약관 동의 확인
    const termsAgreement = document.getElementById("terms-agreement")
    if (!termsAgreement || !termsAgreement.checked) {
      alert(this.termsRequiredMessageValue || 'Please agree to the terms.')
      return
    }
    
    // 결제 수단 확인
    const selectedPaymentMethod = document.querySelector('input[name="payment-method"]:checked')
    if (!selectedPaymentMethod) {
      alert(this.paymentMethodRequiredMessageValue || 'Please select a payment method.')
      return
    }
    
    // 새 결제 수단인 경우 카드 정보 검증
    if (selectedPaymentMethod.value === "new") {
      const cardNumber = document.getElementById("card-number")
      const expiryDate = document.getElementById("expiry-date")
      const cvc = document.getElementById("cvc")
      const cardHolder = document.getElementById("card-holder")
      
      if (!cardNumber || !cardNumber.value.trim()) {
        alert(this.cardNumberRequiredMessageValue || 'Enter card number.')
        return
      }
      
      if (!expiryDate || !expiryDate.value.trim()) {
        alert(this.expiryRequiredMessageValue || 'Enter expiry date.')
        return
      }
      
      if (!cvc || !cvc.value.trim()) {
        alert(this.cvcRequiredMessageValue || 'Enter CVC.')
        return
      }
      
      if (!cardHolder || !cardHolder.value.trim()) {
        alert(this.cardHolderRequiredMessageValue || 'Enter cardholder name.')
        return
      }
    }
    
    // 현재 구독이 있는지 확인
    const currentSubscription = document.querySelector("[data-subscription-current-plan]")
    const isPendingCancellation = document.querySelector("[data-subscription-is-pending-cancellation='true']")
    
    if (currentSubscription && !isPendingCancellation) {
      // 구독 변경 확인 모달 열기
      this.openConfirmModal()
    } else {
      // 신규 구독 처리
      this.submitSubscription()
    }
  }

  // 확인 모달 열기
  openConfirmModal() {
    // 현재 플랜 정보 가져오기
    const currentPlanElement = document.querySelector("[data-subscription-current-plan]")
    const currentPlanName = currentPlanElement?.dataset.subscriptionCurrentPlanName || ""
    const currentPlanPrice = currentPlanElement?.dataset.subscriptionCurrentPlanPrice || 0
    
    // 현재 플랜 이름 설정
    this.currentPlanNameValue = currentPlanName
    
    // 현재 플랜 이름 표시
    if (this.hasCurrentPlanNameTarget) {
      this.currentPlanNameTarget.textContent = currentPlanName
    }
    
    // 새 플랜 이름 표시
    if (this.hasNewPlanNameTarget) {
      this.newPlanNameTarget.textContent = this.planNameValue
    }
    
    // 가격 차이 계산 및 표시
    if (this.hasPriceDifferenceTarget) {
      const priceDiff = Number(this.priceDifferenceValue) || 0
      
      if (priceDiff > 0) {
        this.priceDifferenceTarget.textContent = this.formatCurrency(priceDiff)
      } else {
        this.priceDifferenceTarget.textContent = (this.noExtraCostLabelValue || 'No extra cost')
      }
    }
    
    // 확인 메시지 설정
    if (this.hasConfirmMessageTarget) {
      // 다운그레이드인지 업그레이드인지 확인
      if (Number(this.planPriceValue) > Number(currentPlanPrice)) {
        // 업그레이드
        this.confirmMessageTarget.textContent = (this.upgradeConfirmTextValue || `Upgrade to ${this.planNameValue}?`)
      } else if (Number(this.planPriceValue) < Number(currentPlanPrice)) {
        // 다운그레이드
        this.confirmMessageTarget.textContent = (this.downgradeConfirmTextValue || `Downgrade to ${this.planNameValue}?`)
      } else {
        // 같은 가격의 다른 플랜
        this.confirmMessageTarget.textContent = (this.changeConfirmTextValue || `Change to ${this.planNameValue}?`)
      }
    }
    
    // 모달 열기
    const modal = document.getElementById("confirm-modal")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.open()
      }
    }
    
    // 구독 모달 닫기
    this.closeSubscribeModal()
  }

  // 확인 모달 닫기
  closeConfirmModal() {
    const modal = document.getElementById("confirm-modal")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.close()
      }
    }
  }

  // 구독 변경 확인
  confirmSubscriptionChange() {
    this.submitSubscription()
    this.closeConfirmModal()
  }

  // 구독 신청/변경 제출
  submitSubscription() {
    // 기존 폼을 찾거나 생성
    let form = document.querySelector('#subscription-form')
    
    if (!form) {
      // 폼이 없으면 동적 생성 (하지만 가능하면 HTML에서 제공하는 것이 좋음)
      form = document.createElement("form")
      form.id = "subscription-form"
      form.method = "POST"
      form.action = "/subscriptions"
      form.style.display = "none"
      document.body.appendChild(form)
    }
    
    // 폼 내용 초기화 후 재생성
    form.innerHTML = ""
    
    // CSRF 토큰 추가
    const csrfToken = document.querySelector("meta[name='csrf-token']").content
    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)
    
    // 플랜 ID 추가
    const planIdInput = document.createElement("input")
    planIdInput.type = "hidden"
    planIdInput.name = "subscription_plan_id"
    planIdInput.value = this.planIdValue
    form.appendChild(planIdInput)
    
    // 결제 수단 정보 추가
    const selectedPaymentMethod = document.querySelector('input[name="payment-method"]:checked')
    if (selectedPaymentMethod) {
      const paymentMethodInput = document.createElement("input")
      paymentMethodInput.type = "hidden"
      paymentMethodInput.name = "payment_method_id"
      
      // 새 결제 수단인 경우
      if (selectedPaymentMethod.value === "new") {
        // 카드 정보 추가
        const cardNumber = document.getElementById("card-number")?.value
        const expiryDate = document.getElementById("expiry-date")?.value
        const cvc = document.getElementById("cvc")?.value
        const cardHolder = document.getElementById("card-holder")?.value
        
        if (cardNumber && expiryDate && cvc && cardHolder) {
          const cardInfoInput = document.createElement("input")
          cardInfoInput.type = "hidden"
          cardInfoInput.name = "card_info"
          cardInfoInput.value = JSON.stringify({
            card_number: cardNumber,
            expiry_date: expiryDate,
            cvc: cvc,
            card_holder: cardHolder
          })
          form.appendChild(cardInfoInput)
        }
        
        paymentMethodInput.value = "new"
      } else {
        // 기존 결제 수단 ID 사용
        paymentMethodInput.value = selectedPaymentMethod.value
      }
      
      form.appendChild(paymentMethodInput)
    }
    
    // 현재 구독이 있는지 확인
    const currentSubscription = document.querySelector("[data-subscription-current-plan]")
    if (currentSubscription) {
      // 구독 변경
      const methodInput = document.createElement("input")
      methodInput.type = "hidden"
      methodInput.name = "_method"
      methodInput.value = "patch"
      form.appendChild(methodInput)
      
      // 현재 구독 ID 추가
      const subscriptionIdInput = document.createElement("input")
      subscriptionIdInput.type = "hidden"
      subscriptionIdInput.name = "subscription_id"
      subscriptionIdInput.value = currentSubscription.dataset.subscriptionId || ""
      form.appendChild(subscriptionIdInput)
    }
    
    // Turbo가 폼 제출을 처리하도록 requestSubmit 사용
    form.requestSubmit()
  }
}
